'use client'

import { Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useEffect, useState } from 'react'

export type NotificationType = 'success' | 'error' | 'info'

interface NotificationProps {
  type?: NotificationType
  title: string
  message?: string
  duration?: number
  onClose?: () => void
  show?: boolean
}

const notificationConfig = {
  success: {
    icon: CheckCircleIcon,
    iconColor: 'text-emerald-500 dark:text-emerald-400',
    bgColor: 'bg-white dark:bg-zinc-800',
    borderColor: 'border-emerald-500/20 dark:border-emerald-400/20',
    textColor: 'text-zinc-900 dark:text-white',
    messageColor: 'text-zinc-500 dark:text-zinc-400',
  },
  error: {
    icon: ExclamationCircleIcon,
    iconColor: 'text-red-500 dark:text-red-400',
    bgColor: 'bg-white dark:bg-zinc-800',
    borderColor: 'border-red-500/20 dark:border-red-400/20',
    textColor: 'text-zinc-900 dark:text-white',
    messageColor: 'text-zinc-500 dark:text-zinc-400',
  },
  info: {
    icon: InformationCircleIcon,
    iconColor: 'text-blue-500 dark:text-blue-400',
    bgColor: 'bg-white dark:bg-zinc-800',
    borderColor: 'border-blue-500/20 dark:border-blue-400/20',
    textColor: 'text-zinc-900 dark:text-white',
    messageColor: 'text-zinc-500 dark:text-zinc-400',
  },
}

export default function Notification({
  type = 'success',
  title,
  message,
  duration = 5000,
  onClose,
  show: initialShow = true,
}: NotificationProps) {
  const [show, setShow] = useState(initialShow)

  useEffect(() => {
    // `show` is a dependency so that dismissing by hand tears the timer down.
    // Without it the timer stayed armed after a manual close and fired a second
    // onClose one duration later, against a toast that was already gone.
    if (!show) return
    if (duration > 0) {
      const timer = setTimeout(() => {
        setShow(false)
        onClose?.()
      }, duration)

      return () => clearTimeout(timer)
    }
  }, [duration, onClose, show])

  const handleClose = () => {
    setShow(false)
    onClose?.()
  }

  const config = notificationConfig[type]
  const Icon = config.icon

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <Transition
          show={show}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={clsx(
              'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg shadow-lg ring-1 ring-black/5',
              config.bgColor,
              config.borderColor
            )}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="shrink-0">
                  <Icon aria-hidden="true" className={clsx('size-6', config.iconColor)} />
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className={clsx('text-sm font-medium', config.textColor)}>{title}</p>
                  {message && <p className={clsx('mt-1 text-sm', config.messageColor)}>{message}</p>}
                </div>
                <div className="ml-4 flex shrink-0">
                  <button
                    type="button"
                    onClick={handleClose}
                    className={clsx(
                      'inline-flex rounded-md text-zinc-400 hover:text-zinc-500 focus:outline-hidden',
                      'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                      'dark:text-zinc-500 dark:hover:text-zinc-400'
                    )}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon aria-hidden="true" className="size-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  )
}
